import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type { PartDetailsParams } from "../../utils/getPartDetailsParams";

export type EqpSection = "roof" | "sidewall" | "caster-mold";

/**
 * Minimal shape of a result set table from the API.
 * Works with both the Roof format ({ columns, rows }) and the Sidewall/Caster format ({ rows }).
 */
interface ResultSetTable<TRow = Record<string, unknown>> {
  rows: TRow[];
}

/**
 * Shared response shape for getPartDetails across all sections.
 * The actual PartDetailsResponse differs slightly between sections in TypeScript types,
 * but at runtime they all have tables[0], tables[1], tables[2] with { rows: [] }.
 *
 * Consumers that need typed rows should import their section's specific API types
 * and cast after extraction.
 */
export interface PartDetailsData {
  tables: {
    [index: number]: ResultSetTable;
    0: ResultSetTable;
    1: ResultSetTable;
    2: ResultSetTable;
  };
}

/**
 * Type for a getPartDetails repository function.
 * Each section (roof, sidewall, caster) exports its own getPartDetails
 * with the same signature — this type allows the shared hook to accept any of them.
 */
export type GetPartDetailsFn = (
  partId: number | null,
  parentPartId?: number | null,
  positionId?: number | null,
) => Promise<{
  ok: boolean;
  data?: PartDetailsData;
  message?: string;
}>;

// =============================================================================
// Query key factory — single source of truth for cache key structure.
// Both usePartDetailsQuery and useEnsurePartDetailsData share this.
//
// Flat pattern: ["<section>-details", partId, parentPartId, positionId]
// Prefix matching: invalidate(["roof-details"]) busts all roof detail queries.
// =============================================================================

const partDetailsQueryKey = (
  section: EqpSection,
  params: PartDetailsParams | null,
) => [
  `${section}-details`,
  params?.partId ?? null,
  params?.parentPartId ?? null,
  params?.positionId ?? null,
];

const partDetailsQueryFn =
  (queryFn: GetPartDetailsFn, params: PartDetailsParams) => async () => {
    const resp = await queryFn(
      params.partId,
      params.parentPartId,
      params.positionId,
    );
    if (resp.ok) return resp.data!;
    throw new Error(resp.message ?? "Failed to load part details");
  };

// =============================================================================
// Hooks
// =============================================================================

/**
 * Shared query hook for getPartDetails — used by MetricsPanel and HistoryPanel.
 * Each page passes its own section-specific repository function as queryFn.
 *
 * Both panels can call this with the same params + section — TanStack Query
 * deduplicates into a single API call via matching query keys.
 *
 * Returns the full multi-table response. Consumers use `select` or extract
 * individual result sets (tables[0] = master, tables[1] = metrics, tables[2] = history).
 *
 * @param section - The equipment section ("roof" | "sidewall" | "caster-mold")
 * @param params - Row-derived params from getPartDetailsParams(), or null when no selection
 * @param queryFn - The section-specific getPartDetails repository function
 */
export const usePartDetailsQuery = (
  section: EqpSection,
  params: PartDetailsParams | null,
  queryFn: GetPartDetailsFn,
) => {
  const hasValidParams =
    params !== null &&
    (params.partId !== null ||
      (params.parentPartId !== null && params.positionId !== null));

  return useQuery({
    queryKey: partDetailsQueryKey(section, params),
    queryFn: params
      ? partDetailsQueryFn(queryFn, params)
      : () => {
          throw new Error("No params provided");
        },
    enabled: hasValidParams,
  });
};

/**
 * Returns a function that ensures part details data is available for a given row.
 * Uses queryClient.ensureQueryData:
 * - Cache hit → returns data immediately (no API call)
 * - Fetch in-flight → piggybacks on the existing request
 * - Cache miss → triggers a new fetch
 *
 * Bypasses React render cycle timing — safe to call from event handlers
 * where useQuery's state may not have updated yet.
 */
export const useEnsurePartDetailsData = (
  section: EqpSection,
  queryFn: GetPartDetailsFn,
) => {
  const queryClient = useQueryClient();

  return useCallback(
    (params: PartDetailsParams) => {
      return queryClient.ensureQueryData({
        queryKey: partDetailsQueryKey(section, params),
        queryFn: partDetailsQueryFn(queryFn, params),
      });
    },
    [queryClient, section, queryFn],
  );
};
